import { useMemo } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import type { SinglePayment } from '../../../../services/SinglePayments';
import {
  getSinglePaymentStatusKind,
  getSinglePaymentStatusLabel,
  type SinglePaymentStatusKind
} from './singlePaymentStatus';

type SinglePaymentResultProps = {
  errorMessage?: string | null;
  isLoading?: boolean;
  onRetry?: (() => void) | null;
  payment: SinglePayment | null;
  status: SinglePayment['status'] | null | undefined;
};

const supplierNames: Record<string, string> = {
  dlocal: 'dLocal',
  ebanx: 'Ebanx',
  mercadopago: 'Mercado Pago',
  paypal: 'PayPal',
  transbank: 'Transbank'
};

function getUserFullName(user: SinglePayment['user'] | undefined): string {
  const firstName = user?.name ?? user?.firstname ?? '';
  const lastName = user?.lastname ?? '';
  return `${firstName} ${lastName}`.trim();
}

function getSupplierLabel(supplier: string | null | undefined): string {
  if (!supplier) {
    return 'No informado';
  }

  return supplierNames[String(supplier).toLowerCase()] ?? String(supplier);
}

function getStatusPresentation(statusKind: SinglePaymentStatusKind): {
  description: string;
  heading: string;
  illustration: string;
  supportCopy: string;
  titleClassName: string;
} {
  switch (statusKind) {
    case 'approved':
      return {
        description:
          'Tu pago fue procesado correctamente. Aqui puedes revisar el detalle registrado en la pasarela.',
        heading: 'Pago confirmado',
        illustration: require('../../../../assets/images/pago_finalizado.png'),
        supportCopy:
          'Si tienes dudas sobre este pago o necesitas ayuda con la siguiente cuota, escríbenos a administracion@desafiolatam.com.',
        titleClassName: 'text-green-600'
      };
    case 'rejected':
      return {
        description:
          'El pago no pudo completarse. Revisa la informacion registrada y, si corresponde, intenta nuevamente.',
        heading: 'No pudimos confirmar tu pago',
        illustration: require('../../../../assets/images/pago_rechazado.png'),
        supportCopy:
          'Si necesitas ayuda para reintentar el pago o revisar el rechazo, escríbenos a administracion@desafiolatam.com.',
        titleClassName: 'text-rose-600'
      };
    case 'processing':
      return {
        description:
          'Tu pago esta siendo procesado. Te notificaremos cuando tengamos una respuesta definitiva.',
        heading: 'Estamos validando tu pago',
        illustration: require('../../../../assets/images/pago_finalizado.png'),
        supportCopy:
          'Si necesitas confirmar este pago manualmente, escríbenos a administracion@desafiolatam.com.',
        titleClassName: 'text-amber-600'
      };
    default:
      return {
        description:
          'Todavia no tenemos una confirmacion definitiva del resultado de este pago.',
        heading: 'Estado del pago no confirmado',
        illustration: require('../../../../assets/images/pago_rechazado.png'),
        supportCopy:
          'Si el problema persiste, escríbenos a administracion@desafiolatam.com para revisar el estado del cobro.',
        titleClassName: 'text-slate-700'
      };
  }
}

function formatAmount(
  amount: number | string | null | undefined,
  currencyCode: string | undefined
): string {
  if (amount == null) {
    return 'No informado';
  }

  const numericAmount = Number(amount);
  if (Number.isNaN(numericAmount)) {
    return String(amount);
  }

  if (currencyCode) {
    try {
      return new Intl.NumberFormat('es-CL', {
        currency: currencyCode,
        style: 'currency'
      }).format(numericAmount);
    } catch (error) {
      return `${currencyCode} ${numericAmount.toLocaleString('es-CL')}`;
    }
  }

  return numericAmount.toLocaleString('es-CL');
}

export const SinglePaymentResult = ({
  errorMessage = null,
  isLoading = false,
  onRetry = null,
  payment,
  status
}: SinglePaymentResultProps) => {
  const statusKind = useMemo(
    () => getSinglePaymentStatusKind(status),
    [status]
  );
  const statusLabel = useMemo(
    () => getSinglePaymentStatusLabel(status),
    [status]
  );
  const statusPresentation = useMemo(
    () => getStatusPresentation(statusKind),
    [statusKind]
  );
  const fullName = useMemo(() => getUserFullName(payment?.user), [payment]);

  if (isLoading) {
    return (
      <div className='relative h-[60vh] w-full flex justify-center items-center'>
        <ProgressSpinner />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <section className='w-full max-w-6xl mx-auto'>
        <div className='bg-white border border-gray-300 rounded-lg shadow-sm p-8 md:p-10 text-center'>
          <h1 className='text-2xl md:text-3xl font-semibold text-gray-900'>
            No fue posible cargar el resultado del pago
          </h1>
          <p className='mt-4 text-gray-700 max-w-2xl mx-auto'>
            {errorMessage}
          </p>
        </div>
      </section>
    );
  }

  if (!payment) {
    return (
      <section className='w-full max-w-6xl mx-auto'>
        <div className='bg-white border border-gray-300 rounded-lg shadow-sm p-8 md:p-10 text-center'>
          <h1 className='text-2xl md:text-3xl font-semibold text-gray-900'>
            No hay informacion suficiente para mostrar este resultado
          </h1>
          <p className='mt-4 text-gray-700 max-w-2xl mx-auto'>
            Abre nuevamente el enlace original para continuar con la validacion
            del pago.
          </p>
        </div>
      </section>
    );
  }

  const isApproved = statusKind === 'approved';
  const isRejected = statusKind === 'rejected';

  return (
    <section className='w-full max-w-6xl mx-auto'>
      <div className='bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden'>
        <div className='p-8 text-center border-b border-gray-300 bg-gradient-to-br from-sky-50 to-white'>
          <p className='text-sm font-semibold uppercase tracking-[0.2em] text-sky-700'>
            Pago de Cuota Mensual
          </p>
          <h1
            className={`mt-3 text-2xl md:text-3xl font-semibold ${statusPresentation.titleClassName}`}
          >
            {statusPresentation.heading}
          </h1>
          <p className='mt-3 text-gray-700 max-w-3xl mx-auto'>
            {statusPresentation.description}
          </p>
        </div>

        <div className='p-6 md:p-8'>
          <div className='bg-gray-50 border border-gray-200 rounded-lg p-6'>
            <div className='flex items-center gap-4 mb-6'>
              <div>
                <p className='text-sm text-gray-500'>Pagador</p>
                <h2 className='font-semibold text-xl text-gray-900'>
                  {fullName || 'No informado'}
                </h2>
                <p className='text-gray-600'>
                  {payment.user?.email ?? 'Sin email'}
                </p>
              </div>
            </div>

            <div className='grid gap-4 md:grid-cols-2'>
              <div>
                <h3 className='font-semibold text-gray-900 mb-2'>Programa</h3>
                <p className='text-gray-700'>
                  {payment.generation?.description ?? 'No informado'}
                </p>
              </div>
              <div>
                <h3 className='font-semibold text-gray-900 mb-2'>Proveedor</h3>
                <p className='text-gray-700'>
                  {getSupplierLabel(payment.chosen_supplier)}
                </p>
              </div>
              <div>
                <h3 className='font-semibold text-gray-900 mb-2'>Monto</h3>
                <p className='text-gray-700'>
                  {formatAmount(payment.amount, payment.currency?.code)}
                </p>
              </div>
              <div>
                <h3 className='font-semibold text-gray-900 mb-2'>Moneda</h3>
                <p className='text-gray-700'>
                  {payment.currency?.code ??
                    payment.currency?.name ??
                    'No informada'}
                </p>
              </div>
              <div>
                <h3 className='font-semibold text-gray-900 mb-2'>Estado</h3>
                <p className='text-gray-700'>{statusLabel}</p>
              </div>
              <div>
                <h3 className='font-semibold text-gray-900 mb-2'>Numero de cuota</h3>
                <p className='text-gray-700'>
                  {payment.quote_number && payment.num_quotes
                    ? `${payment.quote_number}/${payment.num_quotes}`
                    : 'No informado'}
                </p>
              </div>
            </div>
          </div>

          <div className='mt-6 bg-sky-50 border border-sky-200 rounded-lg p-6'>
            <div className='flex items-start gap-3'>
              <svg
                className='w-6 h-6 text-sky-600 flex-shrink-0 mt-0.5'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
              <p className='text-gray-700'>
                {statusPresentation.supportCopy}
              </p>
            </div>
          </div>

          <div className='mt-8 flex flex-col items-center justify-between gap-6 border-t border-gray-200 pt-8 lg:flex-row'>
            <div className='text-center lg:text-left'>
              <p className='text-gray-700 mb-2'>
                {isApproved
                  ? 'Felicidades por seguir aceptando el desafío, ahora'
                  : isRejected
                    ? 'Cuando quieras, puedes volver a intentar el pago desde la pasarela.'
                    : 'Mantente atento a la confirmacion final de este cobro.'}
              </p>
              <p
                className={`text-2xl font-bold ${
                  isApproved
                    ? 'text-green-600'
                    : isRejected
                      ? 'text-sky-600'
                      : 'text-amber-600'
                }`}
              >
                {isApproved
                  ? '¡Vamos con todo!'
                  : isRejected
                    ? 'Te esperamos para continuar'
                    : 'Te avisaremos apenas se confirme'}
              </p>
            </div>

            <div className='flex flex-wrap justify-center gap-3'>
              {isRejected && onRetry && (
                <button
                  className='btn'
                  onClick={onRetry}
                  type='button'
                >
                  Reintentar pago
                </button>
              )}
            </div>
          </div>

          <div className='mt-8 flex justify-center'>
            <img
              alt={isApproved ? 'pago_finalizado' : 'pago_rechazado'}
              className='w-full max-w-xl'
              src={statusPresentation.illustration}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
